import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  Check,
  Clock3,
  Globe2,
  KeyRound,
  Mail,
  UserPlus,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { SaaSServices } from '../services/saasServices';
import { apiErrorMessage, unwrapApiData } from '../auth/saasSession';
import { persistAuthLanguage, readAuthLanguage } from '../auth/authLanguage';
import brandMark from '../assets/brand-assets/buoy-mark.svg';
import brandWatermark from '../assets/brand-assets/buoy-watermark-white.png';
import './Login.css';
import './AcceptInvitation.css';

const EMPTY_PROFILE = {
  password: '',
  confirm_password: '',
  full_name: '',
  phone_number: '',
  employee_id: '',
};

const PAGE_COPY = {
  en: {
    dir: 'ltr',
    languageButton: 'عربي',
    languageButtonLabel: 'Switch to Arabic',
    brandLines: ['Disaster Management', 'Smart System'],
    title: 'Join your DisasterMS institution',
    subtitle: 'Your secure invitation connects your account to the institution and departments selected by its administrator.',
    tokenInfo: 'Open the invitation link sent by your Institution Administrator. If you received only a token, paste it below.',
    tokenLabel: 'Invitation token',
    tokenPlaceholder: 'Paste your invitation token',
    tokenRequired: 'Paste the invitation token or open the invitation link sent by your administrator.',
    invitationFailed: 'This invitation could not be opened.',
    continue: 'Continue',
    opening: 'Opening invitation…',
    backToSignIn: 'Back to sign in',
    privacyNote: 'Institutions and department lists are never exposed through public registration.',
    confirmed: 'Invitation confirmed',
    assignedDepartments: 'Assigned departments',
    existingAccount: 'This email already has a DisasterMS account. Accepting adds an inactive membership for this institution; your existing password and profile stay unchanged.',
    fullName: 'Full name',
    employeeId: 'Employee ID',
    phoneNumber: 'Phone number',
    createPassword: 'Create a password',
    confirmPassword: 'Confirm password',
    passwordsMismatch: 'Passwords do not match.',
    acceptFailed: 'Invitation could not be accepted.',
    inactiveNotice: 'Your membership remains inactive until your Institution Administrator activates it.',
    acceptInvitation: 'Accept invitation',
    accepting: 'Accepting…',
    useDifferent: 'Use a different invitation',
    acceptedTitle: 'Invitation accepted',
    acceptedSubtitle: 'Your account is ready and waiting for an institution administrator to activate it.',
    pendingNote: "No named-user seat is consumed until activation — you'll be notified when your account goes live.",
    returnToSignIn: 'Return to sign in',
    allSystems: 'ALL SYSTEMS OPERATIONAL',
  },
  ar: {
    dir: 'rtl',
    languageButton: 'English',
    languageButtonLabel: 'التبديل إلى الإنجليزية',
    brandLines: ['نظام إدارة', 'الأزمات الذكي'],
    title: 'الانضمام إلى مؤسستك في DisasterMS',
    subtitle: 'دعوتك الآمنة تربط حسابك بالمؤسسة والأقسام التي حددها مسؤول مؤسستك.',
    tokenInfo: 'افتح رابط الدعوة المرسل من مسؤول مؤسستك. إذا وصلك رمز فقط، الصقه في الحقل أدناه.',
    tokenLabel: 'رمز الدعوة',
    tokenPlaceholder: 'الصق رمز الدعوة هنا',
    tokenRequired: 'الصق رمز الدعوة أو افتح رابط الدعوة المرسل من مسؤول مؤسستك.',
    invitationFailed: 'تعذر فتح هذه الدعوة.',
    continue: 'متابعة',
    opening: 'جارٍ فتح الدعوة…',
    backToSignIn: 'العودة لتسجيل الدخول',
    privacyNote: 'قوائم المؤسسات والأقسام لا تُعرض أبداً عبر التسجيل العام.',
    confirmed: 'تم التحقق من الدعوة',
    assignedDepartments: 'الأقسام المحددة',
    existingAccount: 'يوجد حساب DisasterMS مرتبط بهذا البريد الإلكتروني. سيؤدي قبول الدعوة إلى إضافة عضوية غير نشطة في هذه المؤسسة، دون تغيير كلمة المرور أو الملف الشخصي الحالي.',
    fullName: 'الاسم الكامل',
    employeeId: 'الرقم الوظيفي',
    phoneNumber: 'رقم الهاتف',
    createPassword: 'أنشئ كلمة مرور',
    confirmPassword: 'تأكيد كلمة المرور',
    passwordsMismatch: 'كلمتا المرور غير متطابقتين.',
    acceptFailed: 'تعذر قبول الدعوة.',
    inactiveNotice: 'ستبقى عضويتك غير نشطة حتى يقوم مسؤول المؤسسة بتفعيلها.',
    acceptInvitation: 'قبول الدعوة',
    accepting: 'جارٍ قبول الدعوة…',
    useDifferent: 'استخدام دعوة أخرى',
    acceptedTitle: 'تم قبول الدعوة',
    acceptedSubtitle: 'حسابك جاهز وبانتظار تفعيل مسؤول المؤسسة.',
    pendingNote: 'لن يُستهلك أي مقعد مستخدم حتى يتم التفعيل — ستصلك رسالة عند تفعيل حسابك.',
    returnToSignIn: 'العودة لتسجيل الدخول',
    allSystems: 'جميع الأنظمة تعمل',
  },
};

const BrandLockup = ({ lines }) => (
  <div className="dms-invitation-brand-lockup" aria-label={lines.join(' ')}>
    <img className="dms-invitation-brand-mark" src={brandMark} alt="" draggable="false" />
    <p className="dms-invitation-wordmark">
      {lines.map((line) => (
        <React.Fragment key={line}>{line}<br /></React.Fragment>
      ))}
    </p>
  </div>
);

const AuthShell = ({ language, text, onLanguageToggle, children }) => (
  <div
    className="dms-login-page dms-invitation-page"
    dir={text.dir}
    lang={language}
    data-testid="disasterms-invitation-page"
  >
    <div className="dms-login-top-line" aria-hidden="true" />
    <div className="dms-login-glow" aria-hidden="true" />
    <div className="dms-login-decor" aria-hidden="true">
      <img className="dms-login-watermark" src={brandWatermark} alt="" draggable="false" />
    </div>

    <button
      type="button"
      className="dms-language-toggle"
      onClick={onLanguageToggle}
      aria-label={text.languageButtonLabel}
    >
      <Globe2 size={15} aria-hidden="true" />
      <span>{text.languageButton}</span>
    </button>

    <main className="dms-invitation-main">
      <div className="dms-invitation-column">
        <BrandLockup lines={text.brandLines} />
        {children}
      </div>
    </main>

    <footer className="dms-login-footer dms-invitation-footer">
      <span className="dms-login-footer-status">
        <span className="dms-login-footer-dot" aria-hidden="true" />
        <span>{text.allSystems}</span>
      </span>
      <span className="dms-login-footer-spacer" aria-hidden="true" />
      <span className="dms-login-footer-copyright" dir="ltr">© 2026 DisasterMS/SOS Ecosystem</span>
    </footer>
  </div>
);

export default function AcceptInvitation() {
  const [params] = useSearchParams();
  const initialToken = params.get('token') || '';
  const [token, setToken] = useState(initialToken);
  const [reviewedToken, setReviewedToken] = useState('');
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [language, setLanguage] = useState(readAuthLanguage);
  const text = PAGE_COPY[language];

  const reviewInvitation = async (candidateToken) => {
    const normalizedToken = String(candidateToken || '').trim();
    if (!normalizedToken) {
      setError(text.tokenRequired);
      return false;
    }
    setLoading(true);
    setError('');
    const response = await SaaSServices.getInvitation(normalizedToken);
    const result = unwrapApiData(response);
    setLoading(false);
    if (!result) {
      setPreview(null);
      setReviewedToken('');
      setError(apiErrorMessage(response, text.invitationFailed));
      return false;
    }
    setToken(normalizedToken);
    setReviewedToken(normalizedToken);
    setPreview(result);
    return true;
  };

  useEffect(() => {
    if (initialToken) reviewInvitation(initialToken);
    // The link token is intentionally reviewed only once on page entry.
  }, [initialToken]);

  const review = async (event) => {
    event.preventDefault();
    await reviewInvitation(token);
  };

  const accept = async (event) => {
    event.preventDefault();
    setError('');
    if (!preview || reviewedToken !== token.trim()) {
      await reviewInvitation(token);
      return;
    }
    if (!preview.existing_account && profile.password !== profile.confirm_password) {
      setError(text.passwordsMismatch);
      return;
    }
    setLoading(true);
    const response = await SaaSServices.acceptInvitation({
      token: reviewedToken,
      ...(!preview.existing_account ? {
        password: profile.password,
        full_name: profile.full_name.trim(),
        phone_number: profile.phone_number.trim(),
        employee_id: profile.employee_id.trim(),
      } : {}),
    });
    const payload = unwrapApiData(response);
    setLoading(false);
    if (!payload?.accepted) {
      setError(apiErrorMessage(response, text.acceptFailed));
      return;
    }
    setAccepted(true);
  };

  const setProfileValue = (key) => (event) => {
    setProfile((old) => ({ ...old, [key]: event.target.value }));
    setError('');
  };

  const toggleLanguage = () => {
    const nextLanguage = language === 'en' ? 'ar' : 'en';
    persistAuthLanguage(nextLanguage);
    setLanguage(nextLanguage);
    setError('');
  };

  const resetInvitation = () => {
    setPreview(null);
    setReviewedToken('');
    setProfile(EMPTY_PROFILE);
    setError('');
  };

  return (
    <AuthShell
      language={language}
      text={text}
      onLanguageToggle={toggleLanguage}
    >
      {accepted ? (
        <section className="dms-invitation-card dms-invitation-success-card" aria-labelledby="invitation-success-title">
          <div className="dms-invitation-success-mark" aria-hidden="true">
            <span className="dms-invitation-success-ring" />
            <Check size={34} strokeWidth={2.5} />
          </div>
          <h1 id="invitation-success-title" className="dms-invitation-success-title">{text.acceptedTitle}</h1>
          <p className="dms-invitation-success-copy">{text.acceptedSubtitle}</p>
          <div className="dms-invitation-pending-note">
            <Clock3 size={18} aria-hidden="true" />
            <span>{text.pendingNote}</span>
          </div>
          <Link className="dms-invitation-return-link" to="/login">
            <span>{text.returnToSignIn}</span>
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </section>
      ) : !preview ? (
        <>
          <section className="dms-invitation-card" aria-labelledby="invitation-token-title">
            <h1 id="invitation-token-title" className="dms-invitation-title">{text.title}</h1>
            <p className="dms-invitation-subtitle">{text.subtitle}</p>

            <div className="dms-invitation-info">
              <Mail size={18} aria-hidden="true" />
              <span>{text.tokenInfo}</span>
            </div>

            <form className="dms-invitation-token-form" onSubmit={review}>
              <label className="dms-auth-label" htmlFor="invitation-token">{text.tokenLabel}</label>
              <div className={`dms-auth-field dms-invitation-token-field${error ? ' dms-invitation-field--error' : ''}`}>
                <KeyRound className="dms-auth-field-icon" size={16} aria-hidden="true" />
                <input
                  id="invitation-token"
                  name="invitation-token"
                  type="text"
                  autoComplete="off"
                  spellCheck="false"
                  className="dms-auth-input dms-invitation-token-input"
                  placeholder={text.tokenPlaceholder}
                  value={token}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? 'invitation-token-error' : undefined}
                  onChange={(event) => {
                    setToken(event.target.value);
                    setError('');
                  }}
                />
              </div>
              {error && <p id="invitation-token-error" className="dms-invitation-inline-error" role="alert">{error}</p>}
              <button className="dms-auth-submit dms-invitation-submit" type="submit" disabled={loading || !token.trim()}>
                {loading ? text.opening : text.continue}
              </button>
            </form>

            <Link className="dms-invitation-back-link" to="/login">{text.backToSignIn}</Link>
          </section>
          <p className="dms-invitation-footnote">{text.privacyNote}</p>
        </>
      ) : (
        <section className="dms-invitation-card dms-invitation-completion-card" aria-labelledby="invitation-completion-title">
          <h1 id="invitation-completion-title" className="dms-invitation-title">{text.title}</h1>
          <p className="dms-invitation-subtitle">{text.subtitle}</p>

          <form className="dms-invitation-completion-form" onSubmit={accept}>
            <div className="dms-invitation-confirmation">
              <p className="dms-invitation-confirmation-label">{text.confirmed}</p>
              <p className="dms-invitation-institution-name">{preview.institution_name}</p>
              <p className="dms-invitation-email" dir="ltr">{preview.email}</p>
              <div className="dms-invitation-departments">
                <p>{text.assignedDepartments}</p>
                <div>
                  {(preview.departments || []).map((department) => (
                    <span key={department}>{department}</span>
                  ))}
                </div>
              </div>
            </div>

            {preview.existing_account ? (
              <p className="dms-invitation-existing-account">{text.existingAccount}</p>
            ) : (
              <div className="dms-invitation-profile-grid">
                <label className="dms-invitation-profile-group">
                  <span className="dms-auth-label">{text.fullName}</span>
                  <span className="dms-auth-field">
                    <input required className="dms-auth-input" placeholder={text.fullName} value={profile.full_name} onChange={setProfileValue('full_name')} />
                  </span>
                </label>
                <label className="dms-invitation-profile-group">
                  <span className="dms-auth-label">{text.employeeId}</span>
                  <span className="dms-auth-field">
                    <input required className="dms-auth-input" placeholder={text.employeeId} value={profile.employee_id} onChange={setProfileValue('employee_id')} />
                  </span>
                </label>
                <label className="dms-invitation-profile-group dms-invitation-profile-group--wide">
                  <span className="dms-auth-label">{text.phoneNumber}</span>
                  <span className="dms-auth-field">
                    <input required type="tel" className="dms-auth-input" placeholder={text.phoneNumber} value={profile.phone_number} onChange={setProfileValue('phone_number')} />
                  </span>
                </label>
                <label className="dms-invitation-profile-group">
                  <span className="dms-auth-label">{text.createPassword}</span>
                  <span className="dms-auth-field">
                    <KeyRound className="dms-auth-field-icon" size={16} aria-hidden="true" />
                    <input required minLength={3} type="password" className="dms-auth-input" placeholder={text.createPassword} value={profile.password} onChange={setProfileValue('password')} />
                  </span>
                </label>
                <label className="dms-invitation-profile-group">
                  <span className="dms-auth-label">{text.confirmPassword}</span>
                  <span className="dms-auth-field">
                    <KeyRound className="dms-auth-field-icon" size={16} aria-hidden="true" />
                    <input required minLength={3} type="password" className="dms-auth-input" placeholder={text.confirmPassword} value={profile.confirm_password} onChange={setProfileValue('confirm_password')} />
                  </span>
                </label>
              </div>
            )}

            <p className="dms-invitation-inactive-notice">{text.inactiveNotice}</p>
            {error && <p className="dms-auth-alert dms-auth-alert--error" role="alert">{error}</p>}
            <button className="dms-auth-submit dms-invitation-submit" type="submit" disabled={loading}>
              <UserPlus size={17} aria-hidden="true" />
              <span>{loading ? text.accepting : text.acceptInvitation}</span>
            </button>
            <button type="button" className="dms-invitation-different-button" onClick={resetInvitation}>{text.useDifferent}</button>
          </form>
        </section>
      )}
    </AuthShell>
  );
}
